import errorHandler from 'common/errorHandler';
import { call, put, takeEvery, takeLatest } from 'redux-saga/effects';
import { Endpoints } from 'services/api';
import * as actions from './actions';

function* createSalesforceConnection(api, connection) {
    let result = yield call([api, api.post], `${Endpoints.systemConnections}/api/v1/salesforce`, connection);
    return result.data;
}

function* startSalesforceOAuth(api, connectionId, returnUrl) {
    let result = yield call([api, api.post], `${Endpoints.systemConnections}/api/v1/salesforce/item/${connectionId}/oauth/start`, { returnUrl });
    return result.data;
}

function* getSalesforceConnection(api, connectionId) {
    try {
        let result = yield call([api, api.get], `${Endpoints.systemConnections}/api/v1/salesforce/item/${connectionId}`);
        return result.data;
    }
    catch (error) {
        if (error.isAxiosError) {
            if (error.response && error.response.status === 404) {
                return null;
            }
        }
        throw error;
    }
}

function* updateSalesforceConnection(api, connectionId, updatedConnection) {
    let response = yield call([api, api.put], `${Endpoints.systemConnections}/api/v1/salesforce/item/${connectionId}`, updatedConnection);
    return response.data;
}

function* deleteSalesforceConnection(api, connectionId) {
    yield call([api, api.delete], `${Endpoints.systemConnections}/api/v1/salesforce/item/${connectionId}`);
}

function* getSalesforceObjects(api, connectionId) {
    let result = yield call([api, api.get], `${Endpoints.systemConnections}/api/v1/salesforce/item/${connectionId}/objects`);
    return result.data;
}

function* getSalesforceObjectDescribe(api, connectionId, objectName) {
    let result = yield call([api, api.get], `${Endpoints.systemConnections}/api/v1/salesforce/item/${connectionId}/objects/${objectName}/describe`);
    return result.data.data;
}

function* getSalesforceObjectPreview(api, connectionId, objectName, fields, limit) {
    let result = yield call([api, api.post], `${Endpoints.systemConnections}/api/v1/salesforce/item/${connectionId}/preview`, { objectName, fields, limit });
    return result.data.data;
}

export function* createSalesforceConnectionRequest(api, { connection }) {
    try {
        let createdConnection = yield call(createSalesforceConnection, api, connection);
        yield put(actions.createSalesforceConnectionSuccess(createdConnection));
    } catch (error) {
        let errorObject = errorHandler(error, "Create", "salesforce connection");
        yield put(actions.createSalesforceConnectionFailure(errorObject));
    }
}

export function* startSalesforceOAuthRequest(api, { connectionId, returnUrl }) {
    try {
        let result = yield call(startSalesforceOAuth, api, connectionId, returnUrl);
        yield put(actions.startSalesforceOAuthSuccess(result));
    } catch (error) {
        let errorObject = errorHandler(error, "Start", "salesforce oauth");
        yield put(actions.startSalesforceOAuthFailure(errorObject));
    }
}

export function* getSalesforceConnectionRequest(api, { connectionId }) {
    try {
        const connection = yield call(getSalesforceConnection, api, connectionId);
        yield put(actions.getSalesforceConnectionSuccess(connection));
    } catch (error) {
        let errorObject = errorHandler(error, "Get", "salesforce connection");
        yield put(actions.getSalesforceConnectionFailure(errorObject));
    }
}

export function* updateSalesforceConnectionRequest(api, { connectionId, updatedConnection }) {
    try {
        const connection = yield call(updateSalesforceConnection, api, connectionId, updatedConnection);
        yield put(actions.updateSalesforceConnectionSuccess(connection));
    } catch (error) {
        let errorObject = errorHandler(error, "Update", "salesforce connection");
        yield put(actions.updateSalesforceConnectionFailure(errorObject));
    }
}

export function* deleteSalesforceConnectionRequest(api, { connectionId }) {
    try {
        yield call(deleteSalesforceConnection, api, connectionId);
        yield put(actions.deleteSalesforceConnectionSuccess(connectionId));
    } catch (error) {
        let errorObject = errorHandler(error, "Delete", "salesforce connection");
        yield put(actions.deleteSalesforceConnectionFailure(errorObject));
    }
}

export function* getSalesforceObjectsRequest(api, { connectionId }) {
    try {
        const objects = yield call(getSalesforceObjects, api, connectionId);
        yield put(actions.getSalesforceObjectsSuccess(objects));
    } catch (error) {
        let errorObject = errorHandler(error, "Get", "salesforce objects");
        yield put(actions.getSalesforceObjectsFailure(errorObject));
    }
}

export function* getSalesforceObjectDescribeRequest(api, { connectionId, objectName }) {
    try {
        const describe = yield call(getSalesforceObjectDescribe, api, connectionId, objectName);
        yield put(actions.getSalesforceObjectDescribeSuccess(describe));
    } catch (error) {
        let errorObject = errorHandler(error, "Get", "salesforce object describe");
        yield put(actions.getSalesforceObjectDescribeFailure(errorObject));
    }
}

export function* getSalesforceObjectPreviewRequest(api, { connectionId, objectName, fields, limit }) {
    try {
        const preview = yield call(getSalesforceObjectPreview, api, connectionId, objectName, fields, limit);
        yield put(actions.getSalesforceObjectPreviewSuccess(preview));
    } catch (error) {
        let errorObject = errorHandler(error, "Get", "salesforce object preview");
        yield put(actions.getSalesforceObjectPreviewFailure(errorObject));
    }
}

export function* watchCreateSalesforceConnectionRequest(api, { params }) {
    yield call(createSalesforceConnectionRequest, api, params);
}

export function* watchStartSalesforceOAuthRequest(api, { params }) {
    yield call(startSalesforceOAuthRequest, api, params);
}

export function* watchGetSalesforceConnectionRequest(api, { params }) {
    yield call(getSalesforceConnectionRequest, api, params);
}

export function* watchUpdateSalesforceConnectionRequest(api, { params }) {
    yield call(updateSalesforceConnectionRequest, api, params);
}

export function* watchDeleteSalesforceConnectionRequest(api, { params }) {
    yield call(deleteSalesforceConnectionRequest, api, params);
}

export function* watchGetSalesforceObjectsRequest(api, { params }) {
    yield call(getSalesforceObjectsRequest, api, params);
}

export function* watchGetSalesforceObjectDescribeRequest(api, { params }) {
    yield call(getSalesforceObjectDescribeRequest, api, params);
}

export function* watchGetSalesforceObjectPreviewRequest(api, { params }) {
    yield call(getSalesforceObjectPreviewRequest, api, params);
}

export default function* ({ api }) {
    yield takeEvery(actions.CREATE_SALESFORCE_CONNECTION_REQUEST, watchCreateSalesforceConnectionRequest, api);
    yield takeEvery(actions.START_SALESFORCE_OAUTH_REQUEST, watchStartSalesforceOAuthRequest, api);
    yield takeLatest(actions.GET_SALESFORCE_CONNECTION_REQUEST, watchGetSalesforceConnectionRequest, api);
    yield takeLatest(actions.UPDATE_SALESFORCE_CONNECTION_REQUEST, watchUpdateSalesforceConnectionRequest, api);
    yield takeLatest(actions.DELETE_SALESFORCE_CONNECTION_REQUEST, watchDeleteSalesforceConnectionRequest, api);
    yield takeLatest(actions.GET_SALESFORCE_OBJECTS_REQUEST, watchGetSalesforceObjectsRequest, api);
    yield takeLatest(actions.GET_SALESFORCE_OBJECT_DESCRIBE_REQUEST, watchGetSalesforceObjectDescribeRequest, api);
    yield takeLatest(actions.GET_SALESFORCE_OBJECT_PREVIEW_REQUEST, watchGetSalesforceObjectPreviewRequest, api);
}
